<template>
  <div>
    <v-navigation-drawer
        style="position:fixed; top:0; right:0; overflow-y:scroll;"
        v-model="openState"
        absolute
        temporary
        right
        width="380px"
    >
      <v-app-bar>
        <v-toolbar-title>Notificações</v-toolbar-title>

        <v-spacer></v-spacer>

        <v-switch
            :disabled="pushButtonDisabled"
            class="mt-5"
            color="accent"
            :loading="loadingSub"
            v-model="isPushEnabled"
            @click="togglePush"
        >
        </v-switch>

        <v-btn icon @click="() => onClose()">
          <v-icon>mdi-close</v-icon>
        </v-btn>
      </v-app-bar>

      <div v-if="loading" class="text-center" style="padding-top: 300px; width: 100%">
        <v-progress-circular
            class="mx-auto my-auto"
            indeterminate
            color="primary"
        ></v-progress-circular>
      </div>
      <div v-else>
        <div class="pa-2">
          <v-card
              v-for="notification in notifications.data"
              :key="notification.id"
              link
              class="mx-auto my-1 "
              @click="() => onClick(notification)"
          >
            <v-card-text>
              <v-card-subtitle :class="`pa-0 ${notification.read ? '' : 'font-weight-bold'}`">
                {{ notification.title }}
              </v-card-subtitle>
              <div :class="`mt-1 ${notification.read ? '' : 'font-weight-bold'}`">
                {{ notification.body }}
              </div>
              <div :class="`${notification.read ? '' : 'font-weight-bold'}`">{{ notification.created_at_humans }}</div>
            </v-card-text>
          </v-card>
        </div>

        <v-btn
            block
            text
            class="ma-2"
            @click="goToNotificationsPage"
        >
          Ver todas
        </v-btn>
      </div>
    </v-navigation-drawer>

    <v-snackbar
        v-model="snackbar.show"
        timeout="3000"
        :color="snackbar.color"
    >
      {{ snackbar.text }}
      <template v-slot:action="{ attrs }">
        <v-btn
            text
            v-bind="attrs"
            @click="snackbar.show = false"
        >
          Ok
        </v-btn>
      </template>
    </v-snackbar>
  </div>
</template>

<script>
import {mapState} from 'vuex'
import {handleResponseError} from '@/api/handleError'
import auth from '@/api/auth'

export default {
  name: 'NotificationsDrawer',
  props: {
    open: {
      type: Boolean,
      required: true
    },
    onClose: {
      type: Function,
      required: true
    }
  },
  computed: {
    ...mapState({
      notifications: state => state.notifications.allWidget,
    })
  },
  data: () => ({
    switchOpen: false,
    notificationsStatus: false,
    openState: false,
    loading: false,
    loadingSub: false,
    snackbar: {
      text: '',
      show: false,
      color: 'info',
    },
    isPushEnabled: false,
    pushButtonDisabled: true
  }),
  mounted() {
    this.registerServiceWorker()
    this.fetchNotifications()
    this.$store.dispatch('layout/getSidebarNotifications')
  },
  methods: {
    showNotification(message, color) {
      this.snackbar.text = message
      this.snackbar.show = true
      this.snackbar.color = color
    },
    fetchNotifications: async function() {
      this.loading = true
      await this.$store.dispatch('notifications/getNotificationsWidget', {
        sort: 'created_at',
        desc: true,
        limit: 15,
        page: 1
      }).catch(e => {
        const message = handleResponseError(e, 'Ocorreu um erro ao buscar as notificações.')
        this.showNotification(message, 'red darken-2')
        this.loading = false
      })
      this.loading = false
    },
    goToNotificationsPage() {
      this.$router.push('/notifications').catch(() => {})
    },
    onClick: function (item) {
      window.location.href = '/notification/' + item.id
    },

    /**
     * Register the service worker.
     */
    registerServiceWorker () {
      if (!('serviceWorker' in navigator)) {
        console.error('Service workers aren\'t supported in this browser.')
        return
      }

      navigator.serviceWorker.register('/messaging-sw.js')
          .then(() => this.initialiseServiceWorker())
    },

    initialiseServiceWorker () {
      if (!('showNotification' in ServiceWorkerRegistration.prototype)) {
        console.error('Notifications aren\'t supported.')
        return
      }

      if (Notification.permission === 'denied') {
        console.error('The user has blocked notifications.')
        return
      }

      if (!('PushManager' in window)) {
        console.error('Push messaging isn\'t supported.')
        return
      }

      navigator.serviceWorker.ready.then(registration => {
        registration.pushManager.getSubscription()
            .then(subscription => {
              this.pushButtonDisabled = false

              if (!subscription) {
                return
              }

              this.updateSubscription(subscription)
              this.isPushEnabled = true
            })
            .catch(e => {
              console.error('Error during getSubscription()', e)
            })
      })
    },

    /**
     * Subscribe for push notifications.
     */
    subscribe () {
      navigator.serviceWorker.ready.then(registration => {
        const options = { userVisibleOnly: true }
        const vapidPublicKey = process.env.VUE_APP_VAPID_PUBLIC_KEY

        if (vapidPublicKey) {
          options.applicationServerKey = this.urlBase64ToUint8Array(vapidPublicKey)
        }

        registration.pushManager.subscribe(options)
            .then(subscription => {
              this.isPushEnabled = true
              this.pushButtonDisabled = false

              this.updateSubscription(subscription)
            })
            .catch(e => {
              if (Notification.permission === 'denied') {
                console.error('Permission for Notifications was denied')
                this.pushButtonDisabled = true
                this.isPushEnabled = false
              } else {
                console.error('Unable to subscribe to push.', e)
                this.isPushEnabled = false
                this.pushButtonDisabled = false
              }
            })
      })
    },

    /**
     * Unsubscribe from push notifications.
     */
    unsubscribe () {
      navigator.serviceWorker.ready.then(registration => {
        registration.pushManager.getSubscription().then(subscription => {
          if (!subscription) {
            this.isPushEnabled = false
            this.pushButtonDisabled = false
            return
          }

          subscription.unsubscribe().then(() => {
            this.deleteSubscription(subscription)

            this.isPushEnabled = false
            this.pushButtonDisabled = false
          }).catch(e => {
            console.error('Unsubscription error: ', e)
            this.pushButtonDisabled = false
          })
        }).catch(e => {
          console.error('Error thrown while unsubscribing.', e)
        })
      })
    },

    /**
     * Toggle push notifications subscription.
     */
    togglePush () {
      if (!this.isPushEnabled) {
        this.unsubscribe()
      } else {
        this.subscribe()
      }
    },

    /**
     * Send a request to the server to update user's subscription.
     *
     * @param {PushSubscription} subscription
     */
    updateSubscription (subscription) {
      const key = subscription.getKey('p256dh')
      const token = subscription.getKey('auth')
      const contentEncoding = (PushManager.supportedContentEncodings || ['aesgcm'])[0]
      const data = {
        endpoint: subscription.endpoint,
        publicKey: key ? btoa(String.fromCharCode.apply(null, new Uint8Array(key))) : null,
        authToken: token ? btoa(String.fromCharCode.apply(null, new Uint8Array(token))) : null,
        contentEncoding
      }

      this.loadingSub = true
      auth.subscribePushNotification(data)
          .then(() => { this.loadingSub = false })
          .catch(() => { this.loadingSub = false })
    },

    /**
     * Send a requst to the server to delete user's subscription.
     *
     * @param {PushSubscription} subscription
     */
    deleteSubscription (subscription) {
      this.loadingSub = true

      auth.unsubscribePushNotification(subscription.endpoint)
          .then(() => { this.loadingSub = false })
          .catch(() => { this.loadingSub = false })
    },

    /**
     * https://github.com/Minishlink/physbook/blob/02a0d5d7ca0d5d2cc6d308a3a9b81244c63b3f14/app/Resources/public/js/app.js#L177
     *
     * @param  {String} base64String
     * @return {Uint8Array}
     */
    urlBase64ToUint8Array (base64String) {
      const padding = '='.repeat((4 - base64String.length % 4) % 4)
      const base64 = (base64String + padding)
          .replace(/-/g, '+')
          .replace(/_/g, '/')

      const rawData = window.atob(base64)
      const outputArray = new Uint8Array(rawData.length)

      for (let i = 0; i < rawData.length; ++i) {
        outputArray[i] = rawData.charCodeAt(i)
      }

      return outputArray
    }
  },
  watch: {
    open(open) {
      this.openState = open
    },
    openState(state) {
      if (state === false) {
        this.onClose()
      } else {
        this.fetchNotifications()
      }
    }
  }
}
</script>
